<template>
    <div class="network-container flex-layout">
      <div class="flex-item">
        <div class="login-card" :class="{ 'simple-box': isM4500 }" id="login_section">
          <div class="login-card-header">
            <div class="header-logo">
              <img id="netgear_logo" src="/static/images/header/logo.png" style="width: 286px;" />
            </div>
            <div class="header-title f16"></div>
          </div>
          <div class="flex-layout">
            <div class="login-card-body flex-item left">
              <el-form
                :model="ruleForm"
                status-icon
                :rules="rules"
                ref="ruleFormRef"
                label-width="100px"
                label-position="top"
                class="login-card-form"
              >
                <el-form-item prop="name">
                  <material-input :label="$t('login.form.username')" size="default">
                    <el-input
                      id="login_name"
                      v-model="ruleForm.name"
                      size="default"
                      @keyup.enter="submitForm"
                    ></el-input>
                  </material-input>
                </el-form-item>
                <el-form-item prop="pass">
                  <material-input :label="$t('login.form.password')" size="default">
                    <el-input
                      id="login_password"
                      v-model="ruleForm.password"
                      size="default"
                      :type="password ? 'password' : 'text'"
                      @keyup.enter="submitForm"
                    >
                      <template #suffix>
                        <i
                          id="password_icon"
                          class="av"
                          :class="{ 'av-eye': !password, 'av-eye-slash': password }"
                          @click="toggleHandler"
                        ></i>
                      </template>
                    </el-input>
                  </material-input>
                </el-form-item>
                <el-form-item>
                  <el-button id="apply_btn" type="primary" @click="submitForm" style="width: 100%;">
                    {{ $t('login.form.login') }}
                  </el-button>
                </el-form-item>
              </el-form>
            </div>
            <div class="login-box flex-item left" v-if="!isM4500">
              <div class="box-header">
                Main UI allows for advanced network configuration setup with additional network switching features.
              </div>
              <el-button id="main_ui" type="primary" style="width: 100%;" class="mt20" @click="oldHandler" :disabled="disableMainUIButton">
                Main UI Login
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>

  <script setup>
  import { ref, reactive, onMounted, computed } from "vue";
  import { useRouter } from "vue-router";
  import { getCurrentInstance } from "vue";
  import { useDeviceStore, useAuthStore } from '@/stores';
  import { formatHostForUrl } from '@/js/util';

  const instance = getCurrentInstance();
  const $api = instance?.appContext.config.globalProperties.$api;
  const $sakura = instance?.appContext.config.globalProperties.$sakura;

  const router = useRouter();
  const deviceStore = useDeviceStore();
  const authStore = useAuthStore();

  const props = defineProps({
    isM4500: Boolean,
  });

  const ruleForm = reactive({
    password: "",
    name: "",
  });

  const password = ref(true);
  const ruleFormRef = ref(null);
  const disableMainUIButton = ref(false);

  const rules = {
    password: [
      {
        validator: (rule, value, callback) => {
          if (value === "") {
            callback(new Error($t("verify.password")));
          } else {
            callback();
          }
        },
        trigger: "blur",
      },
    ],
    name: [
      {
        validator: (rule, value, callback) => {
          if (value === "") {
            callback(new Error($t("verify.username")));
          } else {
            callback();
          }
        },
        trigger: "blur",
      },
    ],
  };

  const toggleHandler = () => {
    password.value = !password.value;
  };

  const oldHandler = () => {
    $api.common.queryHttpPort()
      .then(json => {
        if (json.resp.respCode === 0) {
          const { http, https } = json.ipHttpConfig;
          if(!http.mode && !https.mode){
                        return $sakura.showMessage("Cannot use Main UI when both HTTP and HTTPS are disabled.");
          }
          const protocolScheme = https.mode ? 'https' : 'http';
          const protocol = https.mode ? https : http;
          window.open(`${protocolScheme}://${formatHostForUrl(location.hostname)}:${protocol.port}/v1/base/cheetah_login.html`);
        } else {
          $sakura.sakuraMessage(json.resp.respCode);
        }
      })
      .catch(error => console.error("Error in oldHandler:", error));
  };


  const submitForm = () => {
    ruleFormRef.value.validate(valid => {
      if (valid) {
        loginHandler(ruleForm);
      }
    }).catch(error => {
      console.error("Validation failed:", error);
    });
  };


  const loginHandler = (user) => {

    $api.login.loginPwd({ user })
      .then(json => {
        if (json.resp.respCode === 0) {
          sessionStorage.setItem("token", json.user?.session);
          sessionStorage.setItem("username", user?.name);
          deviceStore.queryDeviceInfo({});
          authStore.queryTerminal({});

          router.push("/configure/overview");
        } else if (json.resp.respCode === 12002) {
          router.push("/change/password");
        } else {
          $sakura.sakuraMessage(json.resp.respCode);
        }
      })
      .catch(error => {
        console.error("Login Error:", error);
        location.reload();
      });
  };


  const initPort = () => {
    try {
      $api.common.queryHttpPort().then(json => {
        if (json.resp.respCode === 0) {
          sessionStorage.setItem('ipHttpConfig', JSON.stringify(json.ipHttpConfig))
          const { http, https } = json.ipHttpConfig;
          if(!http.mode && !https.mode){
            disableMainUIButton.value = true;
          }
          if (https.mode) {
            window.open(`https://${formatHostForUrl(location.hostname)}:4443/#/login`, "_self");
          }
        } else {
          $sakura.sakuraMessage(json.resp.respCode);
        }
      });
    } catch (error) {
      console.error("Error in initPort:", error);
    }
  };

  onMounted(() => {
    initPort();
  });
  </script>

  <style scoped lang="scss">
  .network-container {
    height: 100%;
    align-items: center;
    background-color: #fff;

    .login-card {
      width: 1120px;
      margin: 0 auto;

      .header-title {
        padding: 26px;
        font-weight: 500;
      }

      .login-card-form {
        padding: 30px;
        border-radius: 4px;
        background-color: #fff;
        border: 1px solid #f1f0f0;

        .introduce-color {
          cursor: pointer;
        }
      }

      .login-box {
        margin-left: 20px;
        padding: 20px;
        border: 1px solid #f1f0f0;
      }

      .av {
        padding-right: 5px;
      }
    }
    .simple-box {
      width: 529px;
    }
  }
  </style>
